/**
 * CustomersTable - Customer listing with inline editing
 * Modern CRM style - compact layout
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, AlertCircle, Car } from 'lucide-react';
import { EditableTable, type Column } from '../inline-edit/EditableTable';
import { Input } from '../ui';

/**
 * Customer data for table display
 */
export interface CustomerTableItem {
  id: string;
  name: string;
  email: string;
  phone: string;
  dni?: string | null;
  vehicleCount: number;
  createdAt: number;
  updatedAt: number;
  version?: number;
}

interface CustomersTableProps {
  customers: CustomerTableItem[];
  isLoading: boolean;
  error: Error | null;
  total: number;
  limit: number;
  offset: number;
  search: string;
  onSearchChange: (search: string) => void;
  onPageChange: (offset: number) => void;
  onFieldUpdate: (customerId: string, field: string, value: string) => Promise<void>;
  onUpdateError?: (customerId: string, field: string, error: Error) => void;
  onRowClick?: (customer: CustomerTableItem) => void;
  onEdit?: (customer: CustomerTableItem) => void;
  onDelete?: (customer: CustomerTableItem) => void;
}

export function CustomersTable({
  customers,
  isLoading,
  error,
  total,
  limit,
  offset,
  search,
  onSearchChange,
  onPageChange,
  onFieldUpdate,
  onUpdateError,
  onRowClick,
  onEdit,
  onDelete,
}: CustomersTableProps) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(search);

  // Handle search submit
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange(searchInput);
  }, [searchInput, onSearchChange]);

  // Handle inline save
  const handleSave = useCallback(async (item: CustomerTableItem, field: string, value: string) => {
    await onFieldUpdate(item.id, field, value);
  }, [onFieldUpdate]);

  // Handle inline error
  const handleError = useCallback((item: CustomerTableItem, field: string, err: Error) => {
    onUpdateError?.(item.id, field, err);
  }, [onUpdateError]);

  // Handle row click
  const handleRowClick = useCallback((item: CustomerTableItem) => {
    if (onRowClick) {
      onRowClick(item);
    } else {
      navigate(`/customers/${item.id}`);
    }
  }, [navigate, onRowClick]);

  // Column definitions - compact CRM style
  const columns: Column<CustomerTableItem>[] = [
    {
      key: 'name',
      header: 'Nombre',
      width: '160px',
      editable: {
        type: 'text',
        placeholder: 'Nombre completo',
        getValue: (item) => item.name,
      },
      cellClassName: 'text-xs font-medium text-gray-900',
    },
    {
      key: 'email',
      header: 'Email',
      width: '180px',
      render: (item) => (
        <span className="text-xs text-gray-600">
          {item.email || '-'}
        </span>
      ),
    },
    {
      key: 'phone',
      header: 'Telefono',
      width: '120px',
      editable: {
        type: 'text',
        placeholder: '+34 600 000 000',
        getValue: (item) => item.phone,
      },
      cellClassName: 'text-xs font-mono text-gray-600',
    },
    {
      key: 'dni',
      header: 'DNI/NIE',
      width: '100px',
      render: (item) => (
        <span className="text-xs text-gray-500">
          {item.dni || '-'}
        </span>
      ),
    },
    {
      key: 'vehicleCount',
      header: 'Vehiculos',
      width: '80px',
      render: (item) => (
        <div className="flex items-center gap-1">
          <Car className="w-3 h-3 text-gray-400" />
          <span className="text-xs font-medium text-gray-700">
            {item.vehicleCount}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      width: '70px',
      render: () => (
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
          Activo
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (item) => (
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
              className="text-[11px] text-blue-600 hover:text-blue-800"
            >
              Editar
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
              className="text-[11px] text-red-600 hover:text-red-800"
            >
              Eliminar
            </button>
          )}
        </div>
      ),
    },
  ];

  // Pagination
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="flex items-center gap-3">
        <form onSubmit={handleSearchSubmit} className="flex-1 max-w-sm">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar cliente..."
              className="pl-8 h-8 text-sm"
            />
          </div>
        </form>

        <div className="text-xs text-gray-500">
          {total > 0 ? (
            <>
              {offset + 1}-{Math.min(offset + limit, total)} de {total}
            </>
          ) : (
            'Sin resultados'
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error.message}
        </div>
      )}

      {/* Table */}
      <EditableTable
        columns={columns}
        data={customers}
        getRowKey={(item) => item.id}
        onSave={handleSave}
        onError={handleError}
        loading={isLoading}
        emptyMessage="No hay clientes registrados"
        onRowClick={handleRowClick}
        className="shadow-sm"
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <button
            disabled={offset === 0}
            onClick={() => onPageChange(Math.max(0, offset - limit))}
            className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            Anterior
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + Math.max(1, Math.min(currentPage - 2, totalPages - 4));
              if (pageNum > totalPages) return null;
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange((pageNum - 1) * limit)}
                  className={`w-6 h-6 text-xs rounded ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            disabled={offset + limit >= total}
            onClick={() => onPageChange(offset + limit)}
            className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
